import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button variant + size definitions.
 *
 * Export `buttonVariants` separately from the Button component so consumers
 * who need to style a non-button element with button appearance (e.g. an
 * `<a>` with an `href`) can do `<a className={buttonVariants({ variant: 'primary' })}>`
 * — matches the shadcn/ui convention.
 *
 * Dark-mode notes:
 * - `text-white` is a literal hex (#FFFFFF) — it does NOT flip in dark mode.
 *   Safe to use only when paired with a fixed-value background (e.g.
 *   `bg-accent-500`, `bg-danger-500` — the 500-anchor tokens are unchanged
 *   across themes).
 * - When the background uses a flipping token (e.g. `bg-ink-900`), the text
 *   color must also flip — use `text-ink-50` instead of `text-white`.
 */
export const buttonVariants = cva(
  // Base classes — applied to every variant.
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium ' +
    'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
    'disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      variant: {
        primary:
          'bg-ink-900 text-ink-50 hover:bg-ink-700 ' +
          'focus-visible:ring-ink-900/40',
        secondary:
          'bg-white text-ink-700 border border-ink-300 hover:bg-ink-50 ' +
          'focus-visible:ring-ink-500/40',
        danger:
          'bg-danger-500 text-white hover:bg-danger-600 ' +
          'focus-visible:ring-danger-500/40',
        warning:
          'bg-warning-500 text-white hover:bg-warning-600 ' +
          'focus-visible:ring-warning-500/40',
        ghost:
          'bg-transparent text-ink-700 hover:bg-ink-100 ' +
          'focus-visible:ring-ink-500/40',
        link:
          'bg-transparent text-accent-600 hover:underline ' +
          'focus-visible:ring-accent-500/40 px-0 py-0 h-auto',
      },
      size: {
        sm: 'h-8 min-h-[32px] px-3 text-sm',
        md: 'h-10 min-h-[40px] px-4 text-sm',
        lg: 'h-12 min-h-[48px] px-6 text-base',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Show a spinner and treat the button as disabled. */
  loading?: boolean;
}

const Spinner = () => (
  <span
    className="inline-block h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
    aria-hidden="true"
  />
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading = false, disabled, type = 'button', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        {loading ? <Spinner /> : null}
        {children}
      </button>
    );
  }
);
Button.displayName = 'Button';
